import { useState, useCallback, useRef, useEffect } from "react"

// Web Speech API types (not in all TS libs)
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    SpeechRecognition: any
    webkitSpeechRecognition: any
  }
}

interface VoiceInputProps {
  onTranscript: (text: string) => void
  onPartialTranscript?: (text: string) => void
  className?: string
}

export function VoiceInput({
  onTranscript,
  onPartialTranscript,
  className,
}: VoiceInputProps) {
  const [isListening, setIsListening] = useState(false)
  const [partialText, setPartialText] = useState("")
  const recognitionRef = useRef<any>(null)

  const isSupported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window)

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort()
    }
  }, [])

  const startListening = useCallback(() => {
    if (!isSupported) return

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = "en-US"

    recognition.onresult = (event: any) => {
      let interim = ""
      let final = ""
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript
        } else {
          interim += transcript
        }
      }
      if (interim) {
        setPartialText(interim)
        onPartialTranscript?.(interim)
      }
      if (final) {
        setPartialText("")
        onTranscript(final)
        setIsListening(false)
      }
    }

    recognition.onerror = () => {
      setIsListening(false)
      setPartialText("")
    }

    recognition.onend = () => {
      setIsListening(false)
      setPartialText("")
    }

    recognitionRef.current = recognition
    recognition.start()
    setIsListening(true)
  }, [isSupported, onTranscript, onPartialTranscript])

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop()
    setIsListening(false)
  }, [])

  if (!isSupported) return null

  return (
    <div className={`relative ${className ?? ""}`}>
      <button
        onClick={isListening ? stopListening : startListening}
        className={`
          p-2 rounded-full transition-all duration-200
          ${isListening
            ? "bg-[var(--orca-error)]/20 text-[var(--orca-error)] animate-pulse"
            : "text-[var(--orca-text-muted)] hover:text-[var(--orca-text-secondary)] hover:bg-[var(--orca-bg-hover)]"
          }
        `}
        title={isListening ? "Stop listening" : "Voice input"}
        aria-label={isListening ? "Stop listening" : "Start voice input"}
      >
        <span className="material-icons text-lg">
          {isListening ? "stop_circle" : "mic"}
        </span>
      </button>

      {/* Partial transcript indicator */}
      {isListening && partialText && (
        <div className="absolute bottom-full mb-2 right-0 px-3 py-1.5 rounded-[var(--orca-radius-sm)] bg-[var(--orca-bg-elevated)] border border-[var(--orca-border-subtle)] text-xs text-[var(--orca-text-secondary)] max-w-64 whitespace-nowrap overflow-hidden text-ellipsis shadow-lg">
          {partialText}
        </div>
      )}
    </div>
  )
}
